import React, { useEffect, useRef, useState } from "react";

/* ===========================
   Constants
=========================== */
const MAX_SUPPLY = 21_000_000;
const HALVING_INTERVAL = 210_000;
const INITIAL_BLOCK_REWARD = 50;

/* ===========================
   Props
=========================== */
interface Props {
  percentage: number;   // % emitted
  emittedBtc: number;   // already emitted BTC
  currentBlock: number; // needed for reward calc
  sizeRem?: number;
}

const clamp = (v: number, min = 0, max = 100) =>
  Math.min(Math.max(v, min), max);

const ANIMATION_DURATION = 900;

/* ===========================
   Helpers
=========================== */
const getBlockReward = (block: number) => {
  const halvings = Math.floor(block / HALVING_INTERVAL);
  return INITIAL_BLOCK_REWARD / Math.pow(2, halvings);
};

/* ===========================
   Component
=========================== */
const EmissionGauge: React.FC<Props> = ({
  percentage,
  emittedBtc,
  currentBlock,
  sizeRem = 11.5,
}) => {
  const safePercentage = clamp(percentage);

  /* ===== Animated progress ===== */
  const [animatedProgress, setAnimatedProgress] =
    useState(0);

  const prev = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const start = prev.current;
    const end = safePercentage;
    const startTime = performance.now();

    const animate = (time: number) => {
      const t = Math.min(
        (time - startTime) / ANIMATION_DURATION,
        1
      );

      const eased = 1 - Math.pow(1 - t, 3);
      const value = start + (end - start) * eased;

      setAnimatedProgress(value);

      if (t < 1) {
        frameRef.current =
          requestAnimationFrame(animate);
      } else {
        prev.current = end;
      }
    };

    frameRef.current =
      requestAnimationFrame(animate);

    return () => {
      if (frameRef.current)
        cancelAnimationFrame(frameRef.current);
    };
  }, [safePercentage]);

  /* ===== Calculations ===== */
  const remainingSupply =
    MAX_SUPPLY - emittedBtc;

  const blockReward = getBlockReward(currentBlock);

  /* ===== Geometry ===== */
  const sizePx = sizeRem * 16;
  const radius = sizePx / 2 - 24;
  const circumference = Math.PI * radius;

  const progressArc =
    (animatedProgress / 100) * circumference;

  /* ===== Formatting ===== */
  const formatBtc = (v: number) =>
    v.toLocaleString(undefined, {
      maximumFractionDigits: 0,
    });

  /* ===========================
     Render
  =========================== */
  return (
    <div
      style={{
        width: `${sizeRem}rem`,
        textAlign: "center",
        fontFamily: "Arial, sans-serif",
        color: "#000",
      }}
    >
      <svg
        width={`${sizeRem}rem`}
        height={`${sizeRem / 1.4}rem`}
        viewBox={`0 0 ${sizePx} ${sizePx / 2}`}
      >
        <g transform={`translate(${sizePx / 2}, ${radius + 16})`}>
          {/* Title */}
          <text
            y={-radius - sizePx * 0.11}
            textAnchor="middle"
            fontSize={sizePx * 0.1}
            fontWeight="600"
            fill="#000"
          >
            Emission
          </text>

          {/* Background arc */}
          <path
            d={`M ${-radius} 0 A ${radius} ${radius} 0 0 1 ${radius} 0`}
            fill="none"
            stroke="#FFE0C2"
            strokeWidth={10}
          />

          {/* Progress arc */}
          <path
            d={`M ${-radius} 0 A ${radius} ${radius} 0 0 1 ${radius} 0`}
            fill="none"
            stroke="#F7931A"
            strokeWidth={10}
            strokeDasharray={`${progressArc} ${circumference}`}
            strokeLinecap="butt"
          />

          {/* Percentage */}
          <text
            y={-10}
            textAnchor="middle"
            fontSize={sizePx * 0.12}
            fontWeight="600"
            fill="#000"
          >
            {animatedProgress.toFixed(1)}%
          </text>

          {/* Emitted BTC */}
          <text
            y={sizePx * 0.12}
            textAnchor="middle"
            fontWeight="400"
            fontStyle="italic"
            fill="#475569"
          >
            {formatBtc(emittedBtc)} BTC
          </text>

        </g>
      </svg>

      <div
        style={{
          width: "42%",
          height: "1px",
          margin: "0.1rem auto 0.25rem",
          backgroundColor: "rgba(247, 147, 26, 0.65)",
        }}
      />

      {/* Stats */}
      <div
        style={{
          marginTop: "0.3rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div>
          <span>Block Reward: </span>
          <span style={{ color: "#000" }}>
            <strong>{blockReward.toFixed(3)}</strong>
          </span>
          <br />
          <span>Remaining Supply: </span>
          <br/>
          <span style={{ color: "#000" }}>
            <strong>{formatBtc(remainingSupply)} BTC</strong>
          </span>
        </div>
      </div>

    </div>
  );
};

export default React.memo(EmissionGauge);
